import { createAsyncThunk, createEntityAdapter, createSelector, createSlice, EntityState } from '@reduxjs/toolkit';

import { AppState } from '.';
import {
  exceptionOf,
  Feature,
  Operator,
  ProductType,
  Query,
  SerializedException,
  Subscription,
  SubscriptionSource,
  TrueUpgradeInfo,
} from '../models';
import { SubscriptionService } from '../services';
import { SubscriptionStatus } from '../models/subscriptionStatus';

export const SUBSCRIPTION_FEATURE_KEY = 'subscription';

const subscriptionAdapter = createEntityAdapter<Subscription>();

export interface SubscriptionState extends EntityState<Subscription> {
  // Indicates if the user had any subscription before
  hadSubscription: boolean;
  hadPaidSubscription: boolean;
  loading: boolean;
  errors: SerializedException[];
  trueUpgradeInfo?: TrueUpgradeInfo;
  upgradeToTrue?: boolean;
}

export const createInitialState = (): SubscriptionState => subscriptionAdapter.getInitialState({
  hadSubscription: false,
  hadPaidSubscription: false,
  loading: true,
  errors: [],
});

export const doFetchSubscriptions = createAsyncThunk(
  'subscription/fetchSubscription',
  async (input: { accountId: number }) => {
    const { accountId } = input;
    const criteria: Query<Subscription>[] = [
      {
        field: 'accountId',
        operator: Operator.EQ,
        value: accountId,
      },
      {
        field: 'status',
        operator: Operator.EQ,
        value: SubscriptionStatus.ACTIVE,
      },
    ];
    const subscriptionResponse = await SubscriptionService.getSubscriptions(...criteria);
    return { subscriptionResponse };
  },
);

export const doToggleAutoRenew = createAsyncThunk(
  'subscriptions/toggleAutoRenew',
  async (recurlySubscriptionId: number, { rejectWithValue }) => {
    try {
      const autoRenew = await SubscriptionService.toggleAutoRenew(recurlySubscriptionId);
      return autoRenew;
    } catch (e: any) {
      return rejectWithValue(exceptionOf(e).toJson());
    }
  },
);

export const doReserveUpgradeToTruePlatform = createAsyncThunk(
  'subscriptions/previewUpgradeToTruePlatform',
  async (_, { rejectWithValue }) => {
    try {
      const response = await SubscriptionService.reserveUpgradeToTruePlatform();
      return response.data;
    } catch (e: any) {
      return rejectWithValue(exceptionOf(e).toJson());
    }
  },
);

export const doUpgradeToTruePlatform = createAsyncThunk(
  'subscriptions/upgradeToTruePlatform',
  async (_, { rejectWithValue }) => {
    try {
      const response = await SubscriptionService.upgradeToTruePlatform();
      return response.data;
    } catch (e: any) {
      return rejectWithValue(exceptionOf(e).toJson());
    }
  },
);

export const doGetTruePlatformUpgradeInfo = createAsyncThunk(
  'subscriptions/getTruePlatformUpgradeInfo',
  async (_, { rejectWithValue }) => {
    try {
      const response = await SubscriptionService.getTruePlatformUpgradeInfo();
      return response.data;
    } catch (e: any) {
      return rejectWithValue(exceptionOf(e).toJson());
    }
  },
);

const subscriptionSlice = createSlice({
  name: SUBSCRIPTION_FEATURE_KEY,
  initialState: createInitialState(),
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(doFetchSubscriptions.pending, state => {
        state.loading = true;
      })
      .addCase(doFetchSubscriptions.fulfilled, (state, action) => {
        state.hadSubscription = action.payload.subscriptionResponse.length > 0;
        state.hadPaidSubscription = action.payload.subscriptionResponse.filter(
          sub => sub.source === SubscriptionSource.RECURLY,
        ).length > 0;

        const sevenDaysAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
        const subscriptions = action.payload.subscriptionResponse.filter(
          sub => sub.status === SubscriptionStatus.ACTIVE
            && sub.archived === false
            && sub.expiryDate > sevenDaysAgo,
        );
        subscriptionAdapter.setAll(state, subscriptions);
        state.loading = false;
      })
      .addCase(doFetchSubscriptions.rejected, (state, action) => {
        const payload = action.payload as SerializedException;
        state.loading = false;
        state.errors.push(payload);
      })
      .addCase(doToggleAutoRenew.pending, state => {
        state.loading = true;
      })
      .addCase(doToggleAutoRenew.fulfilled, state => {
        state.loading = false;
      })
      .addCase(doToggleAutoRenew.rejected, (state, action) => {
        state.loading = false;
        state.errors.push(action.payload as SerializedException);
      })
      .addCase(doReserveUpgradeToTruePlatform.pending, state => {
        state.loading = true;
      })
      .addCase(doReserveUpgradeToTruePlatform.fulfilled, (state, action) => {
        state.trueUpgradeInfo = action.payload;
        state.loading = false;
      })
      .addCase(doReserveUpgradeToTruePlatform.rejected, (state, action) => {
        state.loading = false;
        state.errors.push(action.payload as SerializedException);
      })
      .addCase(doUpgradeToTruePlatform.pending, state => {
        state.loading = true;
        state.upgradeToTrue = true;
      })
      .addCase(doUpgradeToTruePlatform.fulfilled, (state, action) => {
        subscriptionAdapter.setAll(state, [action.payload]);
        state.loading = false;
      })
      .addCase(doUpgradeToTruePlatform.rejected, (state, action) => {
        state.loading = false;
        state.errors.push(action.payload as SerializedException);
      })
      .addCase(doGetTruePlatformUpgradeInfo.pending, state => {
        state.loading = true;
      })
      .addCase(doGetTruePlatformUpgradeInfo.fulfilled, (state, action) => {
        state.loading = false;
        state.trueUpgradeInfo = action.payload;
      })
      .addCase(doGetTruePlatformUpgradeInfo.rejected, (state, action) => {
        state.loading = false;
        state.errors.push(action.payload as SerializedException);
      });
  },
});

const selectSubscriptionFeature = (state: AppState) => state[SUBSCRIPTION_FEATURE_KEY];
const { selectAll } = subscriptionAdapter.getSelectors(selectSubscriptionFeature);

export const selectLoading = createSelector(
  selectSubscriptionFeature,
  subState => subState.loading,
);

export const selectErrors = createSelector(
  selectSubscriptionFeature,
  subState => subState.errors,
);

export const selectByAccountId = (accountId: number) => createSelector(
  selectAll,
  subscriptions => subscriptions.filter(it => it.accountId === accountId),
);

export const selectByProductTypes = (
  productTypes: ProductType[],
) => createSelector(
  selectAll,
  subscriptions => subscriptions.filter(
    it => productTypes.includes(it.productType),
  ),
);

export const selectByAccountIdAndFeature = (accountId: number, feature: Feature) => createSelector(
  selectAll,
  subscriptions => subscriptions.filter(
    it => it.accountId === accountId && it.feature === feature,
  ),
);

export const selectSubscriptionsBySource = (
  source: SubscriptionSource,
) => createSelector(
  selectAll,
  subscriptions => subscriptions
    .filter(it => it.source === source),
);

export const selectTrueSubscriptions = (
  source?: SubscriptionSource,
) => createSelector(
  selectAll,
  subscriptions => subscriptions
    .find(it => (!source || it.source === source)
      && (it.feature === Feature.TRUE_TEAM || it.feature === Feature.TRUE_ENTERPRISE)),
);

export const hasPaidSubscription = (accountId: number) => createSelector(
  selectAll,
  subscriptions => subscriptions.some(it => it.accountId === accountId
    && it.source === SubscriptionSource.RECURLY),
);

export const hasTurnOffAutoRenewSub = () => createSelector(
  selectAll,
  subscriptions => subscriptions.some(sub => sub.canceledAt !== undefined),
);

export const selectHadSubscription = createSelector(
  selectSubscriptionFeature,
  subState => subState.hadSubscription,
);

export const selectHadPaidSubscription = createSelector(
  selectSubscriptionFeature,
  subState => subState.hadPaidSubscription,
);

export const selectTrueUpgradeInfo = createSelector(
  selectSubscriptionFeature,
  subState => subState.trueUpgradeInfo,
);

export const selectUpgradeToTrue = createSelector(
  selectSubscriptionFeature,
  subState => subState.upgradeToTrue,
);

// export const {} = subscriptionSlice.actions;
export default subscriptionSlice.reducer;
